import { ReactElement } from 'react';
import ReactBarcode from 'react-barcode';
import { GiHandTruck } from 'react-icons/gi';
import {
  MdLocalShipping,
  MdLocationOn,
  MdStoreMallDirectory,
} from 'react-icons/md';
import { useAuth } from 'hooks';
import { DeliveryModeData } from 'types';
import { Heading, Text } from 'components';
import { getDeliveryDisplay } from 'helpers/get-delivery-display';
import { FontSize, FontWeight } from 'commons';
import * as S from './styles';

interface Props {
  displayData: {
    brandLogo: ReactElement;
    consignmentCode: string;
    isPickupInStore: boolean;
    storeDisplay: {
      name: string;
      street: string;
      postalCode: string;
      district: string;
      fullTown: string;
      renderPhone: ReactElement | null;
    };
    customerDisplay: {
      name: string;
      address: string;
      renderPhone: ReactElement | null;
      complement: string;
    };
    deliveryMode: DeliveryModeData;
  };
}

export function ShippingLabelSmall({ displayData }: Props) {
  const { selectedStore } = useAuth();
  const store = selectedStore?.name || '';

  const {
    brandLogo,
    consignmentCode,
    isPickupInStore,
    storeDisplay,
    customerDisplay,
    deliveryMode,
  } = displayData;

  const deliveryDisplay = getDeliveryDisplay({ store, deliveryMode });

  function renderStore() {
    const { name, street, postalCode, district, fullTown, renderPhone } =
      storeDisplay;

    return (
      <S.HorizontalWrapper>
        <S.StoreSection $position="start">
          <S.Logo>{brandLogo}</S.Logo>

          {isPickupInStore ? (
            <S.Container>
              <MdStoreMallDirectory size={25} />

              <Text size={FontSize.font10} weight={FontWeight.bold}>
                Retire
              </Text>
              <Text size={FontSize.font10} weight={FontWeight.bold}>
                na loja
              </Text>
            </S.Container>
          ) : (
            <S.Container>
              <MdLocalShipping size={24} />

              <Text
                className="special-case"
                size={FontSize.font10}
                weight={FontWeight.bold}
              >
                {deliveryDisplay}
              </Text>
            </S.Container>
          )}
        </S.StoreSection>

        <S.StoreSection $position="end">
          <S.IconWrapper>
            <S.Icon>
              <GiHandTruck />
            </S.Icon>

            <Heading as="h3" size={FontSize.font12} weight={FontWeight.bold}>
              {name}
            </Heading>
          </S.IconWrapper>

          <Text size={FontSize.font10}>{street}</Text>
          <Text size={FontSize.font10}>{postalCode}</Text>
          <Text size={FontSize.font10}>{district}</Text>
          <Text size={FontSize.font10}>{fullTown}</Text>
          <S.PhoneWrapper>{renderPhone}</S.PhoneWrapper>
        </S.StoreSection>
      </S.HorizontalWrapper>
    );
  }

  function renderCustomer() {
    const { name, address, complement, renderPhone } = customerDisplay;

    return (
      <S.Wrapper>
        <S.CustomerSection $startSection>
          <S.IconWrapper>
            <S.Icon>
              <MdLocationOn />
            </S.Icon>

            <Heading as="h3" size={FontSize.font12} weight={FontWeight.bold}>
              {name}
            </Heading>
          </S.IconWrapper>

          <Text size={FontSize.font10}>
            {address}
            {complement ? ` - ${complement}` : ''}
          </Text>
          <S.PhoneWrapper>{renderPhone}</S.PhoneWrapper>
        </S.CustomerSection>

        <S.CustomerSection>
          <ReactBarcode
            value={consignmentCode}
            height={45}
            textPosition="top"
            textAlign="center"
            fontOptions="bold"
            fontSize={12}
            font="arial"
            width={2}
            margin={0}
            marginLeft={3}
            marginRight={3}
          />
        </S.CustomerSection>
      </S.Wrapper>
    );
  }

  return (
    <S.Main>
      {renderStore()}

      {renderCustomer()}
    </S.Main>
  );
}
